import React, { Component } from 'react';
import { connect } from 'react-redux';
import { delay, find, reduce, indexOf, each } from 'lodash';
import shortid from 'shortid';
import PropTypes from 'prop-types';
import Fa from 'react-fontawesome';
import { ListGroup, ListGroupItem, Carousel, Tooltip, OverlayTrigger, Button } from 'react-bootstrap';

import ProgressBar from '../progress-bar';
import { getMeta } from '../../modules/Setup/FilterReducer';
import { fetchAllFilters } from '../../modules/Setup/FilterActions';
import SearchField from '../search-field';

class SearchPath extends Component {
  constructor(props) {
    super(props);
    this.state = {
      index: 0,
      paths: [],
      loading: true,
      forced: false,
      query: {},
      sliding: false,
    };
    this.selectItem = this.selectItem.bind(this);
    this.goBack = this.goBack.bind(this);
    this.selectField = this.selectField.bind(this);
    this.edit = this.edit.bind(this);
  }

  componentDidMount() {
    this.initProps(this.props);
  }

  initProps(props) {
    const { meta, forceReload } = props;
    if (!meta || meta.length === 0 || (forceReload && !this.state.forced)) {
      this.setState({ forced: true }, () => {
          this.props.dispatch(fetchAllFilters())
            .then(
              () => {
                this.initProps(this.props);
              },
              () => {},
            );
        });
    } else {
      let startingNode = find(props.meta, { type: props.startingNode });
      if (startingNode) {
        startingNode = {
          ...startingNode,
          isRoot: true,
        };
      }
      this.setState({
        index: 0,
        loading: false,
        paths: startingNode ? [startingNode] : [],
      });
    }
  }

  selectItem(item) {
    const { sliding } = this.state;
    if (!sliding) {
      const paths = [...this.state.paths, { ...item, $id: shortid.generate() }];
      this.setState({ paths, sliding: true }, () => {
        this.setState({ index: paths.length - 1 }, () => {
          delay(() => {
            this.setState({ sliding: false });
          }, 800);
        });
      });
    }
  }

  selectField(item) {
    const { paths } = this.state;

    const searchPath = reduce(paths, (items, p) => {
      items.push(p.name || p.type);
      return items;
    }, []);

    if (item.type !== 'id' || searchPath.length === 1) {
      // if the last node is not an id
      searchPath.push(item.name || item.type);
    }

    this.props.onUpdate(searchPath, item);
    this.setState({ query: {} });
  }

  edit(e) {
    e.preventDefault();
    // TODO: should we scroll back to the first node?
    this.props.onUpdate([]);
  }

  goBack(e) {
    e.preventDefault();
    const paths = [...this.state.paths];
    if (paths.length > 1) {
      paths.pop();
      this.setState({ index: paths.length - 1, sliding: true }, () => {
        delay(() => {
          this.setState({ paths, sliding: false });
        }, 800);
      });
    }
  }

  renderItem(item) {
    const { paths } = this.state;
    const { meta } = this.props;
    const isObject = item.fields !== undefined && item.fields !== null;
    const props = {
      key: item._id ? `${item._id}` : item.name,
    };
    if (isObject) {
      props.href = "#";
      props.onClick = (e) => {
        e.preventDefault();
        this.selectItem(item);
      };
    }
    const isReference = item.type === 'reference';
    const isForeign = item.is_foreign;

    if (isReference) {
      // do we have this in meta?
      const refTo = isForeign ? item.object?.name : (item.reference_to && item.reference_to.length > 0 ? item.reference_to[0] : null);
      
      const object = find(meta, { type: refTo }) || find(meta, { name: refTo });
      if (object && paths.length < 4) {
        return this.renderItem({
          name: item.name,
          label: item.label,
          objectName: object.name,
          objectLabel: object.label,
          objectType: object.type,
          isReference: true,
          fields: object.fields,
          description: object.description || item.description,
        });
      }
      return null;
    }

    if (!isObject) {
      props.href = "#";
      props.onClick = (e) => {
        e.preventDefault();
        this.selectField(item);
      };
    }

    const { description } = item;
    const hasDescription = description !== null && description !== undefined && description.length > 0;
    const tip = hasDescription ? (
      <Tooltip id={item.name}>{description}</Tooltip>
    ) : null;
    const tipTrigger = hasDescription ? (
      <OverlayTrigger placement="top" overlay={tip}><Fa name="info-circle" className="text-active" /></OverlayTrigger>
    ) : null;

    const name = item.isReference ? <span><strong className="text-active">{(item.objectLabel)}</strong>  {item.label}</span> : item.label;
   
    // const ref = item.isReference ? <div><em className="text-muted">(reference: <code>{item.objectName}</code>)</em></div> : null;

    return (
      <ListGroupItem {...props}>
        <div className="clearfix">
          <div className="pull-right">
            {isObject && <Fa name="chevron-right" />}
            {!isObject && <code>{item.type}</code>}
          </div>
          <div>
            {name} <code>{item.name}</code> {tipTrigger}
          </div>
        </div>
      </ListGroupItem>
    );
  }

  filterNodes(node, searchPattern = null) {
    const fields = node.fields || [];

    // Filter out fields without label or name, currently essential to displaying the field - MW 2024-02-05
    let filteredFields = fields.filter((item) => {
      return (item.reference_to != null && item.reference_to.length > 0) || item.label != null || item.name != null;
    });

    if (this.props.checkFieldType) {
      filteredFields = filteredFields.filter((item) => {
        return item.isValidForSearch === true;
      });
    } else if (this.props.validTypes && this.props.validTypes.length) {
      filteredFields = filteredFields.filter((item) => {
        return indexOf(this.props.validTypes, item.type) >= 0 || item.type === 'reference';
      });
    } else if (this.props.filterNodes) {
      filteredFields = this.props.filterNodes(node);
    }

    if (searchPattern) {
      filteredFields = filteredFields.filter((item) => {
        const refTo = item.reference_to && item.reference_to.length > 0 ? item.reference_to[0] : null;
        return searchPattern.test(item.label) || searchPattern.test(item.name) || (refTo && searchPattern.test(refTo));
      });
    }

    return filteredFields;
  }

  renderNode(node, i) {
    const { meta } = this.props;
    const isRoot = node.isRoot === true;

    const id = node.$id || node.name || node.type;
    const fields = (node.fields || []);

    const hasFields = fields.length > 0;

    const { query } = this.state;
    const keyword = query[id];

    const hasQuery = keyword && keyword !== '' && keyword.length > 0;
    const re = hasQuery ? new RegExp(keyword, 'gi') : null;

    const subItems = this.filterNodes({
      ...node,
      fields,
    } || { fields: [] }, re);

    return (
      <Carousel.Item index={i} key={id}>
        {hasFields &&
          <div>
            <nav className="navbar navbar-default navbar-controls">
              {!isRoot &&
                <div className="navbar-form navbar-left" style={{ paddingTop: '5px' }}>
                  <a href="" onClick={this.goBack}>
                    <Fa name="chevron-left" /> Back
                  </a>
                  {' | '}
                  <strong>{(node.objectLabel || node.name)}</strong>
                </div>
              }
              <div className="navbar-form navbar-left">
                <SearchField onSearch={(q) => {
                  this.setState({ query: { ...this.state.query, [id]: q } });
                }} />
              </div>
            </nav>
            {subItems.length > 0 &&
              <div style={{ borderBottom: '1px solid #ccc', maxHeight: '400px', overflowX: 'hidden', overflowY: 'auto' }}>
                <ListGroup style={{ margin: 0 }}>
                  {subItems.map((item) => this.renderItem(item))}
                </ListGroup>
              </div>
            }
            {subItems.length === 0 &&
              <div>
                {hasQuery && <div className="alert alert-warning">No records found matching <code>{keyword}</code></div>}
                {!hasQuery && <div className="alert alert-warning">No records found</div>}
              </div>
            }
          </div>
        }
        {!hasFields && <div className="alert alert-warning">No fields found.</div>}
      </Carousel.Item>
    );
  }

  renderList() {
    const { paths, index }  = this.state;
    // paths is array of nodes descending from the root, index is of the current node being displayed
    if (!paths || paths.length === 0) {
      return (
        <div className="alert alert-warning">
          No meta found
        </div>
      );
    }
    return (
      <Carousel
        interval={null}
        indicators={false}
        controls={false}
        activeIndex={index}
      >
        {paths.map((node, i) => this.renderNode(node, i))}
      </Carousel>
    );
  }

  renderControl() {
    const { disabled, controlLabel } = this.props;
    return (
      <Button bsStyle="warning" bsSize="xs" onClick={this.edit} disabled={disabled}><Fa name="edit" /> {controlLabel}</Button>
    );
  }

  renderSelectedPaths() {
    const { paths, readOnly } = this.props;
    let control = null;
    if (!readOnly) {
      control = this.renderControl();
    }
    const total = paths.length;

    const items = paths.map((p, i) => {
      const fa = i < total - 1 ? <Fa name="chevron-right" className="text-muted" /> : null;
      return (
        <span key={p}><code>{p}</code> {fa} </span>
      )
    });
    return (
      <div>
        {items} &nbsp; {control}
      </div>
    );
  }

  render() {
    const { loading } = this.state;
    if (loading) {
      return (
        <div>
          <ProgressBar />
        </div>
      );
    }
    const { paths, readOnly } = this.props;
    if (paths && paths.length > 0) {
      return this.renderSelectedPaths();
    }
    if (readOnly) {
      return (
        <div className="text-danger">Missing path info</div>
      );
    }
    return (
      <div>
        {this.renderList()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    meta: getMeta(state),
  };
}

SearchPath.propTypes = {
  dispatch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  paths: PropTypes.arrayOf(PropTypes.string),
  onUpdate: PropTypes.func,
  readOnly: PropTypes.bool,
  startingNode: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    custom: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      reference_to: PropTypes.arrayOf(PropTypes.string),
      sync_required: PropTypes.bool,
      is_custom: PropTypes.bool,
    })).isRequired,
  })).isRequired,
  validTypes: PropTypes.arrayOf(PropTypes.string),
  checkFieldType: PropTypes.bool,
  filterNodes: PropTypes.func,
  forceReload: PropTypes.bool,
  controlLabel: PropTypes.string,
};
SearchPath.defaultProps = {
  paths: null,
  disabled: false,
  readOnly: false,
  startingNode: 'Opportunity',
  checkFieldType: false,
  validTypes: [],
  forceReload: false,
  filterNodes: null,
  controlLabel: 'Edit',
  onUpdate: () => {},
};

export default connect(mapStateToProps)(SearchPath);
